import { useEffect, useRef, type RefObject } from 'react';

const SCROLL_SETTLE_DELAY_MS = 180;
const ACTIONABLE_OVERLAY_SELECTOR = [
  "button",
  "a[href]",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
  "[role='button']",
  "[data-sentry-widget-avoid]",
].join(", ");
export const MANAGED_FLOATING_WIDGET_ATTRIBUTE = 'data-managed-floating-widget';

interface FloatingWidgetSuppressionOptions {
  enabled?: boolean;
  suppressWhenDialogOpen?: boolean;
}

function isElementVisible(element: Element) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.pointerEvents === "none"
  ) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function hasOpenModalLayer() {
  return Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(isElementVisible);
}

function rectanglesOverlap(first: DOMRect, second: DOMRect) {
  return !(
    first.right <= second.left ||
    first.left >= second.right ||
    first.bottom <= second.top ||
    first.top >= second.bottom
  );
}

function overlapsVisibleActionableElement(triggerElement: HTMLElement, triggerRect: DOMRect) {
  const actionables = document.querySelectorAll(ACTIONABLE_OVERLAY_SELECTOR);

  return Array.from(actionables).some((element) => {
    if (triggerElement.contains(element) || !isElementVisible(element)) {
      return false;
    }

    return rectanglesOverlap(triggerRect, element.getBoundingClientRect());
  });
}

export function shouldTemporarilyHideFloatingWidget(
  triggerElement: HTMLElement,
  { suppressWhenDialogOpen = true }: Pick<FloatingWidgetSuppressionOptions, 'suppressWhenDialogOpen'> = {},
) {
  const triggerRect = triggerElement.getBoundingClientRect();
  if (triggerRect.width === 0 || triggerRect.height === 0) {
    return false;
  }

  if (suppressWhenDialogOpen && hasOpenModalLayer()) {
    return true;
  }

  return overlapsVisibleActionableElement(triggerElement, triggerRect);
}

export function applyFloatingWidgetSuppression(triggerElement: HTMLElement, suppressed: boolean) {
  triggerElement.style.transition = "opacity 160ms ease, transform 160ms ease";
  triggerElement.style.opacity = suppressed ? "0.36" : "1";
  triggerElement.style.pointerEvents = suppressed ? "none" : "";
  triggerElement.style.transform = "";
}

export function useFloatingWidgetSuppression(
  ref: RefObject<HTMLElement>,
  { enabled = true, suppressWhenDialogOpen = true }: FloatingWidgetSuppressionOptions = {},
) {
  const scrollTimeoutRef = useRef<number | null>(null);
  const isSuppressedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const updateSuppression = () => {
      const element = ref.current;
      if (!element) {
        return;
      }

      const suppressed = shouldTemporarilyHideFloatingWidget(element, { suppressWhenDialogOpen });
      if (isSuppressedRef.current === suppressed) {
        return;
      }

      applyFloatingWidgetSuppression(element, suppressed);
      isSuppressedRef.current = suppressed;
    };

    const scheduleSuppressionUpdate = () => {
      window.requestAnimationFrame(updateSuppression);
    };

    const scheduleSuppressionUpdateAfterScrollSettles = () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        scrollTimeoutRef.current = null;
        scheduleSuppressionUpdate();
      }, SCROLL_SETTLE_DELAY_MS);
    };

    updateSuppression();

    const resizeObserver = new ResizeObserver(scheduleSuppressionUpdate);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    const mutationObserver = new MutationObserver(scheduleSuppressionUpdate);
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener("resize", scheduleSuppressionUpdate);
    window.addEventListener("scroll", scheduleSuppressionUpdateAfterScrollSettles, true);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", scheduleSuppressionUpdate);
      window.removeEventListener("scroll", scheduleSuppressionUpdateAfterScrollSettles, true);
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      if (ref.current) {
        applyFloatingWidgetSuppression(ref.current, false);
      }
      isSuppressedRef.current = false;
    };
  }, [enabled, ref, suppressWhenDialogOpen]);
}
